/**
 * Catálogo escalável de experiências icónicas curadas (dados versionados).
 *
 * Estrutura genérica e neutra: serve comboios panorâmicos, estradas cénicas,
 * travessias de barco, teleféricos e trilhos, em qualquer país. Cresce por
 * adição de dados — nunca por regras específicas de um destino.
 *
 * Nada é inventado: sem entrada curada, o produto pergunta.
 */

import { fold } from "../geo-scope";

export type TemplateCategory =
  | "scenic_train"
  | "scenic_road"
  | "boat"
  | "cable_car"
  | "trail"
  | "road_trip"
  | "pilgrimage";

export type CuratedRouteTemplate = {
  id: string;
  version: number;
  active: boolean;
  /** ISO alpha-2 dos países atravessados (o primeiro é o principal). */
  countryCodes: string[];
  /** Região(ões) turísticas cobertas. */
  regions: string[];
  category: TemplateCategory;
  /** Nome oficial, apresentado ao utilizador. */
  name: string;
  /** Formas como as pessoas lhe chamam, em vários idiomas. */
  aliases: string[];
  /** Localidades/estações âncora, por ordem. */
  anchors: string[];
  /** Paragens que fazem parte da experiência. */
  requiredPlaces: string[];
  optionalPlaces?: string[];
  /** Duração típica de ponta a ponta, em horas. */
  approxHours: number;
  /** Dias mínimos/máximos de viagem em que faz sentido encaixar. */
  minTripDays: number;
  maxTripDays?: number;
  /** Meio de transporte necessário para a fazer. */
  transport: "train" | "car" | "boat" | "cable_car" | "foot";
  /**
   * Modo real de condução recomendado quando difere do transporte técnico.
   * O Maps continua a calcular como automóvel, mas todas as indicações ao
   * viajante passam a ser dadas nesse modo (ex.: mota numa estrada mítica).
   */
  defaultRideMode?: "motorcycle";
  /** Meses (1–12) em que opera; vazio = todo o ano. */
  seasonMonths?: number[];
  seasonalNote?: string;
  /** Link oficial para horários/bilhetes. */
  officialUrl?: string;
  /** Experiências reais de apoio que permitem cumprir natureza/lagos sem depender de pesquisa externa. */
  supportingExperiences?: Array<{
    name: string;
    city: string;
    category: "lake" | "activity" | "nature" | "mountain" | "coast";
    lat: number;
    lng: number;
    durationMin: number;
    officialUrl?: string;
  }>;
  /**
   * Estradas/troços que compõem a experiência (útil para road trips e mota).
   * Só factos verificáveis: nome oficial, comprimento aproximado, notas.
   */
  roadSegments?: Array<{
    name: string;
    from: string;
    to: string;
    approxKm?: number;
    note?: string;
  }>;
  /** Comprimento total aproximado, em km (rotas rodoviárias). */
  approxKm?: number;
  /** A experiência é adequada a motociclos? (piso, curvas, acesso) */
  motorcycleFriendly?: boolean;
  /** Avisos concretos para quem a faz de mota. */
  motorcycleNotes?: string[];
  note?: string;
  verifiedAt: string;
};

export const CURATED_ROUTE_TEMPLATES: CuratedRouteTemplate[] = [
  {
    id: "ch-glacier-express",
    version: 1,
    active: true,
    countryCodes: ["CH"],
    regions: ["Valais", "Graubünden"],
    category: "scenic_train",
    name: "Glacier Express",
    aliases: [
      "glacier express",
      "comboio dos glaciares",
      "expresso dos glaciares",
      "gletscher express",
    ],
    anchors: ["Zermatt", "Brig", "Andermatt", "Chur", "St. Moritz"],
    requiredPlaces: ["Zermatt", "Chur"],
    optionalPlaces: ["Oberalp Pass", "Landwasser Viaduct"],
    approxHours: 8,
    minTripDays: 3,
    transport: "train",
    seasonalNote:
      "A operação, os segmentos e as reservas variam ao longo do ano; confirma o calendário e os lugares no site oficial.",
    officialUrl: "https://www.glacierexpress.ch/",
    supportingExperiences: [
      {
        name: "Passeio na margem do Lago de St. Moritz",
        city: "St. Moritz",
        category: "lake",
        lat: 46.4908,
        lng: 9.8442,
        durationMin: 90,
        officialUrl: "https://www.engadin.ch/en/activities-experiences/lakes/",
      },
      {
        name: "Caminhada junto ao Lej da Staz",
        city: "St. Moritz",
        category: "lake",
        lat: 46.5037,
        lng: 9.8724,
        durationMin: 90,
        officialUrl: "https://www.engadin.ch/en/activities-experiences/lakes/",
      },
    ],
    note: "Comboio panorâmico mais famoso da Suíça; ocupa praticamente um dia inteiro.",
    verifiedAt: "2026-01-01",
  },
  {
    id: "ch-bernina-express",
    version: 1,
    active: true,
    countryCodes: ["CH", "IT"],
    regions: ["Graubünden", "Valtellina"],
    category: "scenic_train",
    name: "Bernina Express",
    aliases: ["bernina express", "trenino rosso", "comboio bernina", "comboio vermelho"],
    anchors: ["Chur", "St. Moritz", "Tirano"],
    requiredPlaces: ["St. Moritz", "Brusio Circular Viaduct"],
    optionalPlaces: ["Alp Grüm", "Tirano"],
    approxHours: 4,
    minTripDays: 2,
    transport: "train",
    officialUrl: "https://www.rhb.ch/en/panoramic-trains/bernina-express",
    supportingExperiences: [
      {
        name: "Passeio junto ao Lago de St. Moritz",
        city: "St. Moritz",
        category: "lake",
        lat: 46.4908,
        lng: 9.8442,
        durationMin: 90,
        officialUrl: "https://www.engadin.ch/en/activities-experiences/lakes/",
      },
    ],
    note: "Património UNESCO; meio dia é suficiente para o troço principal.",
    verifiedAt: "2026-01-01",
  },
  {
    id: "ch-goldenpass-express",
    version: 1,
    active: true,
    countryCodes: ["CH"],
    regions: ["Lago Léman", "Oberland Bernês"],
    category: "scenic_train",
    name: "GoldenPass Express",
    aliases: ["goldenpass", "golden pass express", "comboio montreux interlaken"],
    anchors: ["Montreux", "Gstaad", "Zweisimmen", "Interlaken"],
    requiredPlaces: ["Montreux", "Interlaken"],
    approxHours: 3.5,
    minTripDays: 2,
    transport: "train",
    officialUrl: "https://journey.mob.ch/en/goldenpass-express",
    supportingExperiences: [
      {
        name: "Passeio na margem do Lago de Thun",
        city: "Interlaken",
        category: "lake",
        lat: 46.6863,
        lng: 7.8632,
        durationMin: 90,
        officialUrl: "https://www.interlaken.ch/en/experiences/lakes-waterfalls/lake-thun",
      },
      {
        name: "Passeio na margem do Lago Léman",
        city: "Montreux",
        category: "lake",
        lat: 46.4312,
        lng: 6.9107,
        durationMin: 90,
        officialUrl: "https://www.montreuxriviera.com/en/",
      },
    ],
    note: "Liga o Lago Léman aos lagos de Interlaken — ideal quando o pedido junta lagos e comboio.",
    verifiedAt: "2026-01-01",
  },
  {
    id: "ch-jungfraujoch",
    version: 1,
    active: true,
    countryCodes: ["CH"],
    regions: ["Oberland Bernês"],
    category: "scenic_train",
    name: "Jungfraujoch — Top of Europe",
    aliases: ["jungfrau", "jungfraujoch", "top of europe"],
    anchors: ["Interlaken", "Grindelwald", "Jungfraujoch"],
    requiredPlaces: ["Kleine Scheidegg", "Jungfraujoch"],
    approxHours: 7,
    minTripDays: 2,
    transport: "train",
    officialUrl: "https://www.jungfrau.ch/",
    note: "Estação ferroviária mais alta da Europa; bilhete caro, dia inteiro.",
    verifiedAt: "2026-01-01",
  },
  {
    id: "ch-lucerne-boat",
    version: 1,
    active: true,
    countryCodes: ["CH"],
    regions: ["Suíça Central"],
    category: "boat",
    name: "Cruzeiro no Lago dos Quatro Cantões",
    aliases: ["barco lucerna", "vierwaldstattersee", "lago lucerna barco"],
    anchors: ["Lucerna", "Vitznau", "Flüelen"],
    requiredPlaces: ["Lucerna", "Vitznau"],
    approxHours: 3,
    minTripDays: 1,
    transport: "boat",
    officialUrl: "https://www.lakelucerne.ch/",
    verifiedAt: "2026-01-01",
  },
  {
    id: "no-flamsbana",
    version: 1,
    active: true,
    countryCodes: ["NO"],
    regions: ["Sognefjord"],
    category: "scenic_train",
    name: "Flåmsbana",
    aliases: ["flam railway", "flamsbana", "comboio de flam"],
    anchors: ["Myrdal", "Flåm"],
    requiredPlaces: ["Flåm", "Kjosfossen"],
    approxHours: 1,
    minTripDays: 2,
    transport: "train",
    officialUrl: "https://www.visitflam.com/",
    supportingExperiences: [
      {
        name: "Passeio pelo Fiorde de Aurlandsfjord",
        city: "Flåm",
        category: "nature",
        lat: 60.863,
        lng: 7.113,
        durationMin: 120,
        officialUrl: "https://www.visitflam.com/",
      },
    ],
    verifiedAt: "2026-01-01",
  },
  {
    id: "it-cinque-terre-express",
    version: 1,
    active: true,
    countryCodes: ["IT"],
    regions: ["Ligúria"],
    category: "scenic_train",
    name: "Cinque Terre Express",
    aliases: ["cinque terre express", "comboio cinque terre"],
    anchors: ["La Spezia", "Riomaggiore", "Monterosso"],
    requiredPlaces: ["Riomaggiore", "Vernazza"],
    approxHours: 1.5,
    minTripDays: 1,
    transport: "train",
    officialUrl: "https://www.trenitalia.com/en/services/cinque-terre-express.html",
    supportingExperiences: [
      {
        name: "Trilho costeiro de Monterosso",
        city: "Monterosso",
        category: "coast",
        lat: 44.1461,
        lng: 9.6549,
        durationMin: 120,
        officialUrl: "https://www.parconazionale5terre.it/",
      },
    ],
    verifiedAt: "2026-01-01",
  },
  {
    id: "is-golden-circle",
    version: 1,
    active: true,
    countryCodes: ["IS"],
    regions: ["Sudoeste"],
    category: "scenic_road",
    name: "Golden Circle",
    aliases: ["golden circle", "círculo dourado"],
    anchors: ["Reiquiavique", "Þingvellir", "Geysir", "Gullfoss"],
    requiredPlaces: ["Þingvellir National Park", "Gullfoss", "Geysir"],
    approxHours: 8,
    minTripDays: 1,
    transport: "car",
    verifiedAt: "2026-01-01",
  },
  {
    id: "pt-linha-do-douro",
    version: 1,
    active: true,
    countryCodes: ["PT"],
    regions: ["Douro"],
    category: "scenic_train",
    name: "Linha do Douro",
    aliases: ["linha do douro", "comboio do douro", "douro line"],
    anchors: ["Porto", "Peso da Régua", "Pinhão"],
    requiredPlaces: ["Estação do Pinhão", "Peso da Régua"],
    approxHours: 3.5,
    minTripDays: 1,
    transport: "train",
    verifiedAt: "2026-01-01",
  },
  {
    id: "gb-west-highland-line",
    version: 1,
    active: true,
    countryCodes: ["GB"],
    regions: ["Highlands", "Escócia Central"],
    category: "scenic_train",
    name: "West Highland Line",
    aliases: ["west highland line", "jacobite", "comboio das highlands", "hogwarts express"],
    anchors: ["Glasgow", "Fort William", "Mallaig"],
    requiredPlaces: ["Glenfinnan Viaduct", "Mallaig"],
    optionalPlaces: ["Rannoch Moor", "Loch Eil"],
    approxHours: 6,
    minTripDays: 2,
    transport: "train",
    seasonalNote:
      "O serviço a vapor (Jacobite) opera sobretudo na época alta; o comboio regular circula todo o ano. Confirma horários e reservas.",
    officialUrl: "https://www.scotrail.co.uk/",
    supportingExperiences: [
      {
        name: "Passeio junto ao Loch Eil",
        city: "Fort William",
        category: "lake",
        lat: 56.839,
        lng: -5.113,
        durationMin: 90,
        officialUrl: "https://www.visitfortwilliam.co.uk/",
      },
    ],
    verifiedAt: "2026-01-01",
  },
  {
    id: "jp-hakone-loop",
    version: 1,
    active: true,
    countryCodes: ["JP"],
    regions: ["Fuji-Hakone"],
    category: "cable_car",
    name: "Hakone Loop (Tozan, Ropeway e barco do Lago Ashi)",
    aliases: ["hakone loop", "hakone tozan", "hakone ropeway", "lago ashi", "ashinoko"],
    anchors: ["Odawara", "Hakone", "Tóquio"],
    requiredPlaces: ["Lake Ashi", "Hakone Ropeway", "Owakudani"],
    approxHours: 7,
    minTripDays: 2,
    transport: "cable_car",
    officialUrl: "https://www.hakonenavi.jp/",
    supportingExperiences: [
      {
        name: "Passeio de barco no Lago Ashi",
        city: "Hakone",
        category: "lake",
        lat: 35.2048,
        lng: 139.025,
        durationMin: 90,
        officialUrl: "https://www.hakonenavi.jp/international/en/transportation/hakone-kankosen",
      },
    ],
    verifiedAt: "2026-01-01",
  },
  {
    id: "jp-sagano-scenic-railway",
    version: 1,
    active: true,
    countryCodes: ["JP"],
    regions: ["Kansai"],
    category: "scenic_train",
    name: "Sagano Romantic Train",
    aliases: ["sagano", "comboio de arashiyama", "sagano romantic train", "torokko"],
    anchors: ["Quioto", "Arashiyama", "Kameoka"],
    requiredPlaces: ["Arashiyama Bamboo Grove", "Hozugawa River"],
    approxHours: 3,
    minTripDays: 2,
    transport: "train",
    seasonalNote: "Encerra habitualmente no inverno; confirma o calendário oficial.",
    verifiedAt: "2026-01-01",
  },
  {
    id: "pt-estrada-nacional-2",
    version: 1,
    active: true,
    countryCodes: ["PT"],
    regions: ["Norte", "Centro", "Alentejo", "Algarve"],
    category: "road_trip",
    name: "Estrada Nacional 2",
    aliases: [
      "estrada nacional 2",
      "nacional 2",
      "en2",
      "n2",
      "route 66 portuguesa",
      "portuguese route 66",
      "national road 2",
    ],
    anchors: ["Chaves", "Vila Real", "Viseu", "Sertã", "Abrantes", "Almodôvar", "Faro"],
    requiredPlaces: ["Chaves", "Faro"],
    optionalPlaces: ["Régua", "Sertã", "Mora", "Torrão"],
    approxHours: 14,
    approxKm: 738,
    // O percurso completo pode ser compactado em quatro etapas progressivas.
    // Assim, numa viagem de cinco dias com regresso incluído, ficam quatro
    // dias para Chaves → Faro e o quinto exclusivamente para regressar.
    minTripDays: 4,
    maxTripDays: 10,
    transport: "car",
    defaultRideMode: "motorcycle",
    motorcycleFriendly: true,
    motorcycleNotes: [
      "Troços de montanha entre Chaves e Viseu com curvas apertadas e piso irregular.",
      "Postos de combustível escassos no Alentejo interior: reabastecer em Abrantes e Almodôvar.",
      "Evitar o percurso com chuva forte nos troços de serra (Marão e Caramulo).",
    ],
    roadSegments: [
      {
        name: "EN2 — Trás-os-Montes",
        from: "Chaves",
        to: "Vila Real",
        approxKm: 65,
        note: "Serra do Alvão, curvas contínuas.",
      },
      { name: "EN2 — Douro e Beiras", from: "Vila Real", to: "Viseu", approxKm: 95 },
      { name: "EN2 — Beira Baixa", from: "Viseu", to: "Sertã", approxKm: 120 },
      {
        name: "EN2 — Alentejo",
        from: "Sertã",
        to: "Almodôvar",
        approxKm: 330,
        note: "Longas retas, planície, calor no verão.",
      },
      {
        name: "EN2 — Algarve",
        from: "Almodôvar",
        to: "Faro",
        approxKm: 90,
        note: "Descida da Serra do Caldeirão.",
      },
    ],
    officialUrl: "https://www.rotan2.pt/",
    note: "738 km de Chaves a Faro, a estrada mais longa de Portugal; existe passaporte oficial carimbado ao longo do percurso.",
    verifiedAt: "2026-01-01",
  },
];

export function templatesForCountry(
  countryCode?: string,
  category?: TemplateCategory,
): CuratedRouteTemplate[] {
  if (!countryCode) return [];
  const code = countryCode.toUpperCase();
  return CURATED_ROUTE_TEMPLATES.filter(
    (t) => t.active && t.countryCodes.includes(code) && (!category || t.category === category),
  );
}

/** Reconhece um template nomeado explicitamente no texto do utilizador. */
export function matchTemplateByName(prompt: string): CuratedRouteTemplate | undefined {
  const text = fold(prompt ?? "");
  return CURATED_ROUTE_TEMPLATES.find(
    (t) => t.active && [t.name, ...t.aliases].some((alias) => text.includes(fold(alias))),
  );
}

/**
 * Opções a apresentar quando o utilizador fala de uma experiência icónica mas
 * não diz qual. Ordena pelo que melhor encaixa nos dias e temas do pedido.
 */
export function suggestTemplates(input: {
  countryCode?: string;
  category?: TemplateCategory;
  days?: number;
  themes?: string[];
  month?: number;
}): CuratedRouteTemplate[] {
  const pool = templatesForCountry(input.countryCode, input.category).filter((t) => {
    if (input.days && t.minTripDays > input.days) return false;
    if (input.days && t.maxTripDays && t.maxTripDays < input.days) return false;
    if (input.month && t.seasonMonths?.length && !t.seasonMonths.includes(input.month))
      return false;
    return true;
  });
  const wanted = new Set(input.themes ?? []);
  const score = (t: CuratedRouteTemplate) => {
    let value = 0;
    if (
      wanted.has("lakes") &&
      /lago|lake|léman|leman|luzern|lucerna|interlaken/i.test(
        `${t.name} ${t.regions.join(" ")} ${t.anchors.join(" ")}`,
      )
    )
      value += 3;
    if (wanted.has("mountains") && /alp|jungfrau|glacier|bernina/i.test(t.name)) value += 2;
    if (input.days && t.approxHours <= input.days * 3) value += 1;
    return value;
  };
  return [...pool].sort((a, b) => score(b) - score(a)).slice(0, 4);
}

/**
 * Auditoria do catálogo: estrutura clara e verificável do que existe hoje,
 * por país e categoria. Usada em testes e no painel do fundador para saber
 * exatamente que rotas conhecidas estão cobertas.
 */
export type CuratedCatalogEntry = {
  id: string;
  name: string;
  category: TemplateCategory;
  countryCodes: string[];
  anchors: string[];
  approxKm?: number;
  motorcycleFriendly: boolean;
  verifiedAt: string;
};

export function listCuratedRoutes(filter?: {
  countryCode?: string;
  category?: TemplateCategory;
  motorcycleOnly?: boolean;
}): CuratedCatalogEntry[] {
  const code = filter?.countryCode?.toUpperCase();
  return CURATED_ROUTE_TEMPLATES.filter((t) => {
    if (!t.active) return false;
    if (code && !t.countryCodes.includes(code)) return false;
    if (filter?.category && t.category !== filter.category) return false;
    if (filter?.motorcycleOnly && !t.motorcycleFriendly) return false;
    return true;
  }).map((t) => ({
    id: t.id,
    name: t.name,
    category: t.category,
    countryCodes: t.countryCodes,
    anchors: t.anchors,
    approxKm: t.approxKm,
    motorcycleFriendly: Boolean(t.motorcycleFriendly),
    verifiedAt: t.verifiedAt,
  }));
}

/** Resumo agregado do catálogo (por categoria) para auditoria rápida. */
export function curatedCatalogSummary(): Array<{
  category: TemplateCategory;
  count: number;
  countries: string[];
}> {
  const map = new Map<TemplateCategory, { count: number; countries: Set<string> }>();
  for (const t of CURATED_ROUTE_TEMPLATES) {
    if (!t.active) continue;
    const entry = map.get(t.category) ?? { count: 0, countries: new Set<string>() };
    entry.count += 1;
    for (const c of t.countryCodes) entry.countries.add(c);
    map.set(t.category, entry);
  }
  return [...map.entries()].map(([category, v]) => ({
    category,
    count: v.count,
    countries: [...v.countries].sort(),
  }));
}
